import type { ContextType } from 'react';
import { useEffect, useRef, startTransition } from 'react';

import { useStore } from 'react-admin';
import { useLocation } from 'react-router-dom';

import { RESOURCES } from 'src/views/Resources';

import type { ResourcesContext } from '@ragu-components/ord-components';
import type { useStoreResult } from 'react-admin';
import type { Location } from 'react-router-dom';

export const visitedResourcesStorageKey = 'visitedResources';

/** Extract the resource from the location path
 * Compares with the resources in the RESOURCES object
 * Returns the resource name if it matches
 */
export const getResourceFromLocation = (
  location: Location,
  resources: ContextType<typeof ResourcesContext>
): string | undefined => {
  const matches = location.pathname.match(/^\/([^/]+)(?:\/([^/]+))?/); // Matches first and second path segments after /
  if (matches === null) {
    return undefined;
  }
  const [_match0, match1, match2] = matches;
  const resourcesKeys = Object.keys(resources);
  const filteredResources = resourcesKeys.filter((resource) => match1 !== undefined && resource.startsWith(match1));

  if (filteredResources.length === 1) {
    return filteredResources[0];
  } else if (filteredResources.length > 1 && match2 !== undefined) {
    const resource = filteredResources.find((resource) => resource.endsWith(match2));
    return resource;
  } else {
    return undefined;
  }
};

/** Hook to store visitedResources in the React-admin store and get defaults from local storage */
export const useVisitedResourcesStore = (): useStoreResult<Record<string, number>> => {
  return useStore<Record<string, number>>(
    visitedResourcesStorageKey,
    JSON.parse(localStorage.getItem(visitedResourcesStorageKey) ?? '{}') as Record<string, number>
  );
};

/** Hook to store visitedResources in local storage */
const useVisitedResources = (): void => {
  const storageKey = visitedResourcesStorageKey;
  const [_visitedResources, setVisitedResources] = useVisitedResourcesStore();
  const location = useLocation();
  const previousResourceRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const resourceFromPath = getResourceFromLocation(location, RESOURCES);
    if (resourceFromPath !== undefined && previousResourceRef.current !== resourceFromPath) {
      previousResourceRef.current = resourceFromPath;
      startTransition(() => {
        setVisitedResources((visitedResources) => {
          const updatedVisitedResources = { ...visitedResources };
          const visitedResourceValue = updatedVisitedResources[resourceFromPath] ?? 0;
          updatedVisitedResources[resourceFromPath] = visitedResourceValue + 1;
          localStorage.setItem(storageKey, JSON.stringify(updatedVisitedResources));
          return updatedVisitedResources;
        });
      });
    }
  }, [location, setVisitedResources, storageKey]);
};

export default useVisitedResources;
